import axios from "axios";

// Interface for DOJ order objects
interface DOJOrder {
    id?: string | number;
    isActive?: boolean | string | number;
    summary?: string;
    released_at?: string | number;
    updated_at?: string | number;
    releasedAt?: string;
    updatedAt?: string;
    [key: string]: any;
}

// Helper function to process DOJ orders data
function processOrdersData(doj_orders: DOJOrder[] = []): DOJOrder[] {
    if (!Array.isArray(doj_orders) || doj_orders.length === 0) return [];
    return doj_orders.map((order: DOJOrder) => {
        let releasedAt: string | undefined = undefined;
        let updatedAt: string | undefined = undefined;
        if (order.released_at && typeof order.released_at === "string" && !isNaN(Date.parse(order.released_at))) {
            releasedAt = order.released_at;
        }
        if (order.updated_at && typeof order.updated_at === "string" && !isNaN(Date.parse(order.updated_at))) {
            updatedAt = order.updated_at;
        }
        return {
            ...order,
            isActive:
                order.isActive === true ||
                order.isActive === "true" ||
                order.isActive === 1,
            summary: typeof order.summary === "string" && order.summary.trim().length > 0
                ? order.summary
                : "No summary available",
            releasedAt,
            updatedAt,
        };
    });
}

/**
    * A function that is triggered when the site is loaded to fetch the penal code from Core.
    * @param setPenalCode `Function` - The save state function for the penal code storage.
    * @param setDOJOrders `Function` - The save state function for the DOJ orders storage.
    * @param setLoading `Function` - The state update function to indicate loading
    * @returns `void` - This returns nothing.
    * @example FetchCoreData(setPenalCode, setDOJOrders, setLoading);
*/
export const FetchCoreData = async (
    setPenalCode: Function,
    setDOJOrders: Function,
    setLoading: Function = () => {}
) => {
    setLoading(true);

    try {
        // EXECUTE REQUEST
        const coreApiRequest = await axios.post(
            `https://core.productionrp.org/api/penalcode?Token=${import.meta.env.VITE_CORE_API_KEY}`
        );

        // VALIDATE THE RETURN OF THE API REQUEST
        if (
            coreApiRequest &&
            typeof coreApiRequest === "object" &&
            coreApiRequest["status"] &&
            coreApiRequest["status"] === 200
        ) {
            const returnData = coreApiRequest["data"];
            if (returnData && typeof returnData === "object") {
                setLoading(false);

                // Separate penal code and DOJ orders
                const { charges, tickets, doj_orders } = returnData;
                setPenalCode({ charges, tickets });

                // Ensure `isActive` and `summary` flags are set for each order
                const processedOrders = processOrdersData(doj_orders);
                setDOJOrders(processedOrders);

                return;
            }
        }

        throw new Error("Invalid response structure");
    } catch (error) {
        setLoading(false);

        // Log detailed error for debugging
        console.error("Error fetching core data:", error);

        // RETURN DEFAULT FALLBACK
        setPenalCode({});
        setDOJOrders([]);
    }
};
